import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  attendanceApi,
  employeeApi,
  holidayRequestApi,
  shiftMarketplaceApi,
  sickLeaveRequestApi,
  unavailabilityRequestApi,
  type ManagerInboxItem,
} from '../services/api';
import type {
  AttendanceRecord,
  Employee,
  HolidayRequest,
  ShiftMarketplaceRequest,
  SickLeaveRequest,
  UnavailabilityRequest,
} from '../types';
import { getLicenseExpiryStatus, LICENSE_STATUS_STYLES, daysUntilExpiry } from '../constants/healthcareLicenseTypes';
import { formatCompanyDate, formatCompanyDateRange, formatCompanyDateTime } from '../utils/companyDateFormat';
import Timecards, { type TimecardsEmbedMode } from '../pages/Timecards';
import EmployeeLicensesTab from './EmployeeLicensesTab';

type Props = {
  item: ManagerInboxItem;
  onClose: () => void;
  onTimecardApproved?: () => void;
  onInboxFixed?: () => void;
};

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 break-words">{value}</dd>
    </div>
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return formatCompanyDateTime(value);
}

export function inboxDetailsLabel(type: string) {
  if (type === 'licence_expired' || type === 'licence_expiring') return 'Licenses';
  if (type === 'timecard') return 'View timecard';
  if (type === 'holiday' || type === 'sick' || type === 'unavailability') return 'Request details';
  if (type === 'visa_expired' || type === 'visa_expiring') return 'Update visa';
  if (type === 'marketplace') return 'Marketplace request';
  if (type === 'absent_today' || type === 'missed_clock_out' || type === 'late_today') return 'Attendance details';
  return 'View details';
}

function InboxModalShell({
  title,
  subtitle,
  onClose,
  wide,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inbox-details-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-white rounded-xl shadow-xl w-full flex flex-col overflow-hidden border border-gray-200 ${
          wide ? 'max-w-[96vw] h-[92vh]' : 'max-w-2xl max-h-[90vh]'
        }`}
      >
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="min-w-0">
            <h2 id="inbox-details-title" className="text-base font-semibold text-gray-900 truncate">
              {title}
            </h2>
            {subtitle ? <p className="text-xs text-gray-500 truncate">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-200/80"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className={`flex-1 min-h-0 ${wide ? '' : 'overflow-y-auto p-4'}`}>{children}</div>
      </div>
    </div>
  );
}

const InboxItemDetailsModal: React.FC<Props> = ({ item, onClose, onTimecardApproved, onInboxFixed }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (item.type === 'timecard') {
    const weekStartDate = (item.occurredAt || '').slice(0, 10);
    const embed: TimecardsEmbedMode = {
      employeeId: item.employeeId,
      weekStartDate: weekStartDate || new Date().toISOString().slice(0, 10),
      locationId: item.locationId ?? null,
      onApproved: onTimecardApproved,
    };
    return (
      <InboxModalShell title={`Timecard — ${item.employeeName}`} subtitle={item.why} onClose={onClose} wide>
        <Timecards embed={embed} />
      </InboxModalShell>
    );
  }

  if (item.type === 'licence_expired' || item.type === 'licence_expiring') {
    return (
      <InboxModalShell title={`Licenses — ${item.employeeName}`} subtitle={item.why} onClose={onClose} wide>
        <div className="h-full overflow-y-auto p-4 space-y-3">
          <p className="text-sm text-gray-600">
            {item.viewerRole === 'owner'
              ? 'Renew or replace the highlighted license. This item stays open until that license is valid for more than 30 days, or is removed.'
              : 'You can review the licenses here. Take over this item to edit or resolve it.'}
          </p>
          <EmployeeLicensesTab
            employeeId={item.employeeId}
            highlightLicenseId={item.entityId}
            readOnly={item.viewerRole !== 'owner'}
            onChanged={(rows) => {
              const row = item.entityId != null ? rows.find((l) => l.id === item.entityId) : undefined;
              if (!row || daysUntilExpiry(row.expiryDate) > 30) onInboxFixed?.();
            }}
          />
        </div>
      </InboxModalShell>
    );
  }

  return (
    <InboxModalShell
      title={`${inboxDetailsLabel(item.type)} — ${item.employeeName}`}
      subtitle={item.title}
      onClose={onClose}
    >
      {item.type === 'visa_expired' || item.type === 'visa_expiring' ? (
        <VisaInboxEditor
          employeeId={item.employeeId}
          onSaved={onInboxFixed}
          readOnly={item.viewerRole !== 'owner'}
        />
      ) : item.type === 'holiday' ? (
        <HolidayDetails entityId={item.entityId} />
      ) : item.type === 'sick' ? (
        <SickDetails entityId={item.entityId} />
      ) : item.type === 'unavailability' ? (
        <UnavailabilityDetails entityId={item.entityId} />
      ) : item.type === 'marketplace' ? (
        <MarketplaceDetails entityId={item.entityId} />
      ) : item.type === 'absent_today' || item.type === 'missed_clock_out' || item.type === 'late_today' ? (
        <AttendanceDetails entityId={item.entityId} fallbackWhy={item.why} />
      ) : (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Type" value={item.type} />
          <Field label="Employee" value={item.employeeName} />
          <Field label="Position" value={item.positionTitle} />
          <Field label="Location" value={item.locationName} />
          <Field label="Why" value={item.why} />
          <Field label="Recommended" value={item.recommended} />
        </dl>
      )}
    </InboxModalShell>
  );
};

function LoadingBlock() {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin" />
      Loading details…
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{message}</p>;
}

function VisaInboxEditor({
  employeeId,
  onSaved,
  readOnly = false,
}: {
  employeeId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}) {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visaNumber, setVisaNumber] = useState('');
  const [visaExpiryDate, setVisaExpiryDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    employeeApi
      .getById(employeeId)
      .then((row) => {
        if (cancelled) return;
        setEmployee(row);
        setVisaNumber(row.visaNumber || '');
        setVisaExpiryDate(row.visaExpiryDate ? row.visaExpiryDate.split('T')[0] : '');
      })
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load visa details.');
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  if (error) return <ErrorBlock message={error} />;
  if (!employee) return <LoadingBlock />;

  const expiry = employee.visaExpiryDate;
  const status = expiry ? getLicenseExpiryStatus(expiry) : null;
  const unchanged =
    (visaNumber || '') === (employee.visaNumber || '') &&
    (visaExpiryDate || '') === (expiry ? expiry.split('T')[0] : '');

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visaNumber.trim() || !visaExpiryDate) return;
    setSaving(true);
    setError(null);
    setSavedNote(null);
    try {
      await employeeApi.update(employeeId, {
        visaNumber: visaNumber.trim(),
        visaExpiryDate,
      });
      if (daysUntilExpiry(visaExpiryDate) > 30) {
        onSaved?.();
      } else {
        setEmployee((current) =>
          current
            ? { ...current, visaNumber: visaNumber.trim(), visaExpiryDate }
            : current,
        );
        setSavedNote('Saved. This Inbox item stays open until the new expiry is more than 30 days away.');
      }
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e2?.response?.data?.message || e2?.message || 'Could not save visa details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Current visa</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Employee" value={`${employee.firstName} ${employee.lastName}`} />
          <Field label="Email" value={employee.email} />
          <Field label="Visa number" value={employee.visaNumber || '—'} />
          <Field label="Visa expiry" value={expiry ? formatCompanyDate(expiry) : '—'} />
          {status && (
            <Field
              label="Status"
              value={
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${LICENSE_STATUS_STYLES[status.tone].badge}`}>
                  {status.label}
                </span>
              }
            />
          )}
        </dl>
      </div>

      {readOnly ? (
        <p className="text-sm text-gray-600">Take over this item to enter new visa details and resolve it.</p>
      ) : (
      <form onSubmit={(e) => void save(e)} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
        <div>
          <p className="text-sm font-semibold text-gray-900">New visa details</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Enter the renewed visa. The item is Resolved only when the new expiry is more than 30 days away.
          </p>
        </div>
        {savedNote && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{savedNote}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm text-gray-700">
            New visa number
            <input
              type="text"
              required
              value={visaNumber}
              onChange={(e) => setVisaNumber(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <label className="block text-sm text-gray-700">
            New expiry date
            <input
              type="date"
              required
              value={visaExpiryDate}
              onChange={(e) => setVisaExpiryDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || unchanged || !visaNumber.trim() || !visaExpiryDate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save visa'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}

function HolidayDetails({ entityId }: { entityId?: number | null }) {
  const [row, setRow] = useState<HolidayRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) {
      setError('No holiday request is linked to this item.');
      return;
    }
    let cancelled = false;
    holidayRequestApi
      .getById(entityId)
      .then((data) => {
        if (!cancelled) setRow(data);
      })
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load holiday request.');
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (error) return <ErrorBlock message={error} />;
  if (!row) return <LoadingBlock />;

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Status" value={row.status} />
      <Field label="Leave type" value={row.leaveType?.name || '—'} />
      <Field label="Dates" value={formatCompanyDateRange(row.startDate, row.endDate)} />
      <Field label="Days" value={row.totalDays != null ? String(row.totalDays) : '—'} />
      <Field label="Hours" value={row.totalHours != null ? String(row.totalHours) : '—'} />
      <Field label="Half day" value={row.isHalfDay ? row.halfDayType || 'Yes' : 'No'} />
      <Field label="Reason" value={row.reason || '—'} />
      <Field label="Submitted" value={formatDateTime(row.createdAt)} />
      <Field label="Rejection reason" value={row.rejectionReason} />
    </dl>
  );
}

function SickDetails({ entityId }: { entityId?: number | null }) {
  const [row, setRow] = useState<SickLeaveRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) {
      setError('No sick leave request is linked to this item.');
      return;
    }
    let cancelled = false;
    sickLeaveRequestApi
      .getById(entityId)
      .then((data) => {
        if (!cancelled) setRow(data);
      })
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load sick leave.');
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (error) return <ErrorBlock message={error} />;
  if (!row) return <LoadingBlock />;

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Status" value={row.status} />
      <Field label="Dates" value={formatCompanyDateRange(row.startDate, row.endDate)} />
      <Field label="Days" value={String(row.totalDays)} />
      <Field label="Reason" value={row.reason || '—'} />
      <Field label="Certificate required" value={row.certificateRequired ? 'Yes' : 'No'} />
      <Field label="Certificate uploaded" value={row.certificateUploaded ? row.certificateFileName || 'Yes' : 'No'} />
      <Field label="Reported" value={formatDateTime(row.reportedAt || row.createdAt)} />
    </dl>
  );
}

function UnavailabilityDetails({ entityId }: { entityId?: number | null }) {
  const [row, setRow] = useState<UnavailabilityRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) {
      setError('No unavailability request is linked to this item.');
      return;
    }
    let cancelled = false;
    unavailabilityRequestApi
      .getById(entityId)
      .then((data) => {
        if (!cancelled) setRow(data);
      })
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load unavailability.');
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (error) return <ErrorBlock message={error} />;
  if (!row) return <LoadingBlock />;

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Status" value={row.status} />
      <Field label="Kind" value={row.kind} />
      <Field
        label="Dates"
        value={row.endDate ? formatCompanyDateRange(row.startDate, row.endDate) : formatCompanyDate(row.startDate)}
      />
      <Field label="All day" value={row.isAllDay ? 'Yes' : 'No'} />
      <Field label="Time" value={!row.isAllDay && row.startTime ? `${row.startTime}–${row.endTime || ''}` : '—'} />
      <Field label="Days of week" value={row.daysOfWeek || '—'} />
      <Field label="Reason" value={row.reason || '—'} />
      <Field label="Summary" value={row.summary} />
    </dl>
  );
}

function MarketplaceDetails({ entityId }: { entityId?: number | null }) {
  const [row, setRow] = useState<ShiftMarketplaceRequest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) {
      setError('No marketplace request is linked to this item.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const pending = await shiftMarketplaceApi.getRequests({ approvals: true });
        let found = pending.find((r) => r.id === entityId) ?? null;
        if (!found) {
          const all = await shiftMarketplaceApi.getRequests();
          found = all.find((r) => r.id === entityId) ?? null;
        }
        if (!cancelled) {
          if (found) setRow(found);
          else setError('Marketplace request was not found.');
        }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Could not load marketplace request.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (error) return <ErrorBlock message={error} />;
  if (!row) return <LoadingBlock />;

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Type" value={row.requestType} />
      <Field label="Status" value={row.status} />
      <Field label="Location" value={row.locationName} />
      <Field label="Offered by" value={row.offeredByName} />
      <Field
        label="Offered shift"
        value={
          row.offeredShift
            ? `${row.offeredShift.scheduleName || 'Shift'} ${formatDateTime(row.offeredShift.startTime)}–${new Date(row.offeredShift.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : '—'
        }
      />
      <Field label="Notes" value={row.notes} />
      <Field label="Submitted" value={formatDateTime(row.createdAt)} />
    </dl>
  );
}

function AttendanceDetails({ entityId, fallbackWhy }: { entityId?: number | null; fallbackWhy: string }) {
  const [row, setRow] = useState<AttendanceRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) {
      setError(null);
      return;
    }
    let cancelled = false;
    attendanceApi
      .getById(entityId)
      .then((data) => {
        if (!cancelled) setRow(data);
      })
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Could not load attendance.');
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  if (!entityId) {
    return (
      <dl className="grid grid-cols-1 gap-4">
        <Field label="Summary" value={fallbackWhy} />
      </dl>
    );
  }
  if (error) {
    return (
      <div className="space-y-3">
        <ErrorBlock message={error} />
        <p className="text-sm text-gray-700">{fallbackWhy}</p>
      </div>
    );
  }
  if (!row) return <LoadingBlock />;

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Clock in" value={formatDateTime(row.clockInTime)} />
      <Field label="Clock out" value={row.clockOutTime ? formatDateTime(row.clockOutTime) : 'Still open'} />
      <Field label="Source" value={row.clockInSource || '—'} />
      <Field label="Station" value={row.timeStation?.name} />
      <Field label="Clock-in address" value={row.clockInLocation?.address} />
      <Field label="Summary" value={fallbackWhy} />
    </dl>
  );
}

export default InboxItemDetailsModal;
